import { html, LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import type { UserState } from '../../../app/package/base/service/activation-flow/activation-flow-domain';
import { LoginStatus } from '../../../app/package/base/service/login/login-domain';
import { SubHelper } from '../../base/package/patterns/package/store';
import baseThemeStyle from '../../base/package/themes/de-games/base-theme.css?inline';
import { isMobileWidth } from '../../base/package/util/device';
import { paintTick } from '../../base/package/util/render';
import { shouldShowMiniGameModal } from '../../mod-incentive/helper/mini-game-helpers';
import type { IncentiveMiniGame, IncentiveRewardToClaim } from '../../mod-incentive/types';
import styles from './ui-gamification-icon.css?inline';

const CName = 'ui-gamification-icon';

/**
 * @prop {Boolean} active - Whether the icon is active or not.
 */
@customElement(CName)
export class UiGamificationIcon extends LitElement {
  static readonly styles = [unsafeCSS(styles), unsafeCSS(baseThemeStyle)];

  @property({ type: Boolean }) isActive = false;

  @state() private _hasWheelSpin = false;
  @state() private _hasRewardToClaim = false;
  @state() private _hasNotification = false;

  // When true, render() returns nothing for one frame to force animation restart
  private _suppressRender = false;

  private readonly _subHelper = new SubHelper();
  private readonly _assets = {
    iconId: window.staticAssets.gamification?.iconId,
    disabledImg: window.staticAssets.gamification?.disabledImg,
    enabledImg: window.staticAssets.gamification?.enabledImg,
  };

  private _isIncentiveAvailable = false;

  private readonly _onUserStateChange = (_userState: UserState) => {
    this._updateWheelSpin();
  };

  private readonly _onMiniGameUpdate = (data: IncentiveMiniGame | null) => {
    this._updateMiniGame(data);
  };

  private readonly _onRewardUpdate = (data: any) => {
    this._updateRewardToClaim(data);
  };

  willUpdate(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('isActive') && changedProperties.get('isActive') !== undefined) {
      // isActive changed after initial set — suppress this frame to force animation restart
      this._suppressRender = true;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._isIncentiveAvailable = !!(window.$app.appConfig.gamanzaClientId.length && window.$app.incentiveApi);
    this._initGamification();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.$app.activationFlow.unsubscribeUserState(this._onUserStateChange);
    this._subHelper.unsubscribeAll();
  }

  updated(_changedProperties: PropertyValues<this>) {
    if (this._suppressRender) {
      this._suppressRender = false;
      // Schedule re-render after a paint tick so DOM is cleared for one frame
      paintTick().then(() => {
        this.requestUpdate();
      });
    }
  }

  render() {
    if (this._suppressRender) {
      return nothing;
    }
    const classes = classMap({
      wrapper: true,
      active: this.isActive,
      notification: this._hasNotification || this._hasRewardToClaim,
      mobile: isMobileWidth(),
      'wheel-of-fortune': this._hasWheelSpin,
      'has-reward': this._hasRewardToClaim,
    });
    return html`
      <div class=${classes}>
        <div class="border"></div>
        ${this._renderContentContainer((this.isActive ? this._assets.enabledImg : this._assets.disabledImg) ?? '')}
        ${this._renderLogo(this._assets.iconId)}
        ${this.isActive && this._hasNotification ? html`<div class="shining"></div>` : nothing}
      </div>
    `;
  }

  private _renderLogo(iconId?: string) {
    if (iconId === undefined) {
      return nothing;
    }
    return html`<ui-sprite-svg class="logo" .iconID=${iconId}></ui-sprite-svg>`;
  }

  private _renderContentContainer(srcJpg: string) {
    return html`
      <div class="container">
        ${srcJpg ? html`<img class="media" src=${srcJpg} alt="">` : html`<div class="animation"></div>`}
      </div>
    `;
  }

  private async _initGamification() {
    // Subscribe to compliance state to gate the wheel animation
    window.$app.activationFlow.subscribeUserState(this._onUserStateChange, true);

    // gamanza enabled?
    if (this._isIncentiveAvailable) {
      const isLoggedIn = (await window.$app.login.getFinalLoginStatus()) === LoginStatus.LOGGED_IN;
      if (!this.isConnected) return;
      if (isLoggedIn) {
        this._subHelper.addSub(window.$app.incentiveApi.signal.miniGame, this._onMiniGameUpdate, true);
        this._subHelper.addSub(window.$app.incentiveApi.signal.incentiveRewards, this._onRewardUpdate, true);
      }
    }
  }

  private _updateMiniGame(_data: IncentiveMiniGame | null) {
    this._updateWheelSpin();
  }

  private _updateWheelSpin() {
    const wasAvailable = this._hasWheelSpin;
    this._hasWheelSpin = shouldShowMiniGameModal();
    if (!wasAvailable && this._hasWheelSpin) {
      window.$app.track.gamificationGames({
        eventAction: 'WOF',
        WOF_action: 'WOF_available',
      });
    }
  }

  private _updateRewardToClaim(data: IncentiveRewardToClaim[] | null) {
    const wasAvailable = this._hasRewardToClaim;
    this._hasRewardToClaim = !!data?.length;
    if (!wasAvailable && this._hasRewardToClaim) {
      window.$app.track.gamificationGames({ eventAction: 'new_offers_available' });
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UiGamificationIcon;
  }
}
